import { useState, useEffect, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonIcon from "@mui/icons-material/Person";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import { useAuth } from "contexts/AuthContext";
import { API_BASE, API_HOST } from "config/api";

const WA_GREEN = "#25D366";

// Converts local Ghana numbers (0XX…) to international wa.me format (233XX…).
const toWaPhone = (phone) => {
  const d = (phone || "").replace(/\D/g, "");
  return d.startsWith("0") ? "233" + d.slice(1) : d;
};

function MemberAvatar({ member }) {
  const photo = member?.photo;
  return (
    <Avatar
      src={photo ? `${API_HOST}/storage/${photo}` : undefined}
      sx={{ width: 32, height: 32, bgcolor: WA_GREEN }}
    >
      {!photo && <PersonIcon sx={{ fontSize: 18 }} />}
    </Avatar>
  );
}
MemberAvatar.propTypes = { member: require("prop-types").object };

export default function WhatsAppMessages() {
  const { church } = useAuth();

  const [members, setMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [messageType, setMessageType] = useState("single");
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [message, setMessage] = useState("");
  const [pendingLinks, setPendingLinks] = useState([]);

  const loadMembers = useCallback(async () => {
    if (!church?.id) return;
    setIsLoadingMembers(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await window.fetch(`${API_BASE}/members/church/${church.id}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    } finally {
      setIsLoadingMembers(false);
    }
  }, [church?.id]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const allSelected = useMemo(
    () => members.length > 0 && selectedMembers.length === members.length,
    [members, selectedMembers]
  );

  const handleSelectAll = () => {
    setSelectedMembers(allSelected ? [] : [...members]);
  };

  const handleSend = () => {
    if (!message.trim()) {
      Swal.fire("No message", "Please type a message before sending.", "warning");
      return;
    }

    if (messageType === "single") {
      if (!selectedMember) {
        Swal.fire("No recipient", "Please select a member.", "warning");
        return;
      }
      const cleaned = toWaPhone(selectedMember.phone);
      if (!cleaned) {
        Swal.fire(
          "No phone number",
          `${selectedMember.first_name} has no phone number on file.`,
          "warning"
        );
        return;
      }
      window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`, "_blank");
    } else {
      const targets = selectedMembers.filter((m) => toWaPhone(m.phone));
      if (!targets.length) {
        Swal.fire(
          "No phone numbers",
          "None of the selected members have phone numbers on file.",
          "warning"
        );
        return;
      }
      const links = targets.map((m) => ({
        id: m.id,
        name: `${m.first_name} ${m.surname}`,
        phone: m.phone,
        url: `https://wa.me/${toWaPhone(m.phone)}?text=${encodeURIComponent(message)}`,
      }));
      setPendingLinks(links);
      window.open(links[0].url, "_blank");
      if (links.length > 1) {
        Swal.fire({
          icon: "info",
          title: "Bulk WhatsApp",
          html: `Opened WhatsApp for <strong>${links[0].name}</strong>.<br/>
                 Use the list below to send to the remaining ${
                   links.length - 1
                 } member(s) one at a time.`,
        });
      }
    }
  };

  const charCount = message.length;
  const charColor = charCount > 900 ? "error" : charCount > 700 ? "warning.main" : "text.secondary";

  return (
    <Box>
      <PageHeader
        title="WhatsApp Messages"
        subtitle="Send WhatsApp messages to individual members or groups."
      />

      <Card sx={{ mb: 3, maxWidth: 800 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: WA_GREEN }}>
            Compose Message
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel id="wa-type-label">Message Type</InputLabel>
            <Select
              labelId="wa-type-label"
              value={messageType}
              label="Message Type"
              onChange={(e) => {
                setMessageType(e.target.value);
                setSelectedMember(null);
                setSelectedMembers([]);
                setPendingLinks([]);
              }}
              sx={{ minHeight: 48 }}
            >
              <MenuItem value="single">Single Member</MenuItem>
              <MenuItem value="bulk">Bulk (Multiple Members)</MenuItem>
            </Select>
          </FormControl>

          {messageType === "single" ? (
            <Autocomplete
              options={members}
              loading={isLoadingMembers}
              getOptionLabel={(m) =>
                `${m.first_name} ${m.surname}${m.phone ? ` (${m.phone})` : ""}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedMember}
              onChange={(_, val) => setSelectedMember(val)}
              renderOption={(props, option) => (
                <Box component="li" {...props} display="flex" gap={1.5} alignItems="center">
                  <MemberAvatar member={option} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.first_name} {option.surname}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.phone || "No phone"}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Recipient"
                  margin="normal"
                  placeholder="Search member..."
                  required
                />
              )}
            />
          ) : (
            <Box>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={members}
                loading={isLoadingMembers}
                getOptionLabel={(m) =>
                  `${m.first_name} ${m.surname}${m.phone ? ` (${m.phone})` : ""}`
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedMembers}
                onChange={(_, val) => setSelectedMembers(val)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.id}
                      label={`${option.first_name} ${option.surname}`}
                      size="small"
                      avatar={<MemberAvatar member={option} />}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} display="flex" gap={1.5} alignItems="center">
                    <MemberAvatar member={option} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.first_name} {option.surname}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.phone || "No phone"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Recipients"
                    margin="normal"
                    placeholder="Search members..."
                    helperText={`${selectedMembers.length} of ${members.length} members selected`}
                  />
                )}
              />
              <Button size="small" onClick={handleSelectAll} sx={{ mt: 0.5 }}>
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            </Box>
          )}

          <Box sx={{ position: "relative", mt: 1 }}>
            <TextField
              label="Message"
              multiline
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              margin="normal"
              required
              inputProps={{ maxLength: 1000 }}
            />
            <Typography
              variant="caption"
              sx={{ position: "absolute", bottom: 12, right: 12, color: charColor }}
            >
              {charCount}/1000
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<WhatsAppIcon />}
            onClick={handleSend}
            sx={{
              mt: 2,
              bgcolor: WA_GREEN,
              "&:hover": { bgcolor: "#1da851" },
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              py: 1.5,
            }}
          >
            {messageType === "single" ? "Open in WhatsApp" : "Send via WhatsApp"}
          </Button>
        </CardContent>
      </Card>

      {pendingLinks.length > 0 && (
        <Card sx={{ maxWidth: 800 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, color: WA_GREEN }}>
              Bulk Send Queue
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click each button to open WhatsApp for that member. Browsers allow one tab at a time.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={1.5}>
              {pendingLinks.map((link, i) => (
                <Box
                  key={link.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography
                      variant="caption"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        bgcolor: WA_GREEN,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Typography>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {link.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {link.phone}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => window.open(link.url, "_blank")}
                    sx={{ color: WA_GREEN, border: "1px solid", borderColor: WA_GREEN }}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button size="small" color="inherit" onClick={() => setPendingLinks([])} sx={{ mt: 2 }}>
              Clear Queue
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
